<template>
  <div class="flex flex-wrap -mx-5">
    <div class="w-full md:w-2/3 px-5">
      <p class="font-bold text-2xl mb-3">
        Key Lesson Information
      </p>
      <div class="flex flex-wrap -mx-5">
        <d-field class="w-2/3 px-5" label="Title">
          <d-input v-model="newContentListing.name" required />
        </d-field>

        <d-field class="w-1/3 px-5" label="Cost">
          <d-input v-model.number="computedCost" type="number" step="any" />
        </d-field>

        <d-field class="w-full px-5" label="Description">
            <l-html-editor v-model="newContentListing.description" />
        </d-field>

        <d-field label="Completion Time (in hours)" class="w-full px-5">
          <d-input v-model.number="completionLength" type="number" step="any" />
        </d-field>

        <d-field label="Type" class="w-full px-5">
          <d-select v-model="newContentListing.type" required expanded>
            <option value="Static">
              Static
            </option>
            <option value="Interactive">
              Interactive
            </option>
          </d-select>
        </d-field>

        <d-field v-if="newContentListing.type === ContentListingType.INTERACTIVE && newContentListing.grantsCertificate" label="Certificate Text" class="w-full px-5">
          <d-input v-model="newContentListing.certificateText" />
        </d-field>

        <d-field label="Categories" class="w-1/2 px-5">
          <div>
            <d-select v-model="selectedCategoryId" expanded>
              <option value="" disabled>
                Select a Category
              </option>
              <option v-for="category in categoryOptions" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </d-select>
            <div class="my-3 flex flex-wrap">
              <d-chip v-for="category in newContentListing.categories" :key="category.id" size="sm" label closable class="mr-2 mb-3" @close="removeCategory(category.id)">
                {{ category.name }}
              </d-chip>
            </div>
          </div>
        </d-field>

        <d-field label="Resource Types" class="w-1/2 px-5">
          <div>
            <d-select v-model="selectedResourceTypeId" expanded>
              <option value="" disabled>
                Select a Resource Type
              </option>
              <option v-for="resourceType in resourceTypeOptions" :key="resourceType.id" :value="resourceType.id">
                {{ resourceType.name }}
              </option>
            </d-select>
            <div class="my-3 flex flex-wrap">
              <d-chip v-for="resourceType in newContentListing.resourceTypes" :key="resourceType.id" size="sm" label closable class="mr-2 mb-3" @close="removeResourceType(resourceType.id)">
                {{ resourceType.name }}
              </d-chip>
            </div>
          </div>
        </d-field>

        <div class="w-full px-5 flex justify-end mt-3">
          <d-btn :loading="saving" @click="saveChanges()">
            Save Changes
          </d-btn>
        </div>
      </div>
    </div>

    <div class="w-full md:w-1/3 px-5">
      <p class="font-bold text-2xl mb-3">
        Quick Settings
      </p>
      <d-switch v-model="newContentListing.active" class="w-full ml-0 mb-3">
        Active
      </d-switch>
      <d-switch v-model="newContentListing.public" class="w-full ml-0 mb-3">
        Public
      </d-switch>
      <d-switch v-if="newContentListing.type === ContentListingType.INTERACTIVE" v-model="newContentListing.grantsCertificate" class="w-full ml-0 mb-3">
        Grants Certificate
      </d-switch>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import { ContentListingType } from '@resources/scripts/constants';
  import { showErrorToast } from '@services/errorHandling.service';

  export default {
    inject: ['contentListing', 'getContentListingInfo'],
    data() {
      return {
        ContentListingType,
        saving: false,
        categories: [],
        resourceTypes: [],
        newContentListing: this.contentListing,
        selectedCategoryId: '',
        selectedResourceTypeId: '',
      };
    },

    computed: {
      computedCost: {
        get() {
          return this.newContentListing.cost;
        },
        set(val) {
          this.newContentListing.cost = val ? Number.parseFloat(val) : 0;
        },
      },

      completionLength: {
        get() {
          return this.newContentListing.completionLength;
        },
        set(val) {
          this.newContentListing.completionLength = val.toString();
        },
      },

      categoryOptions() {
        return this.categories.filter((m) => !this.newContentListing.categories.find((x) => x.id === m.id));
      },

      resourceTypeOptions() {
        return this.resourceTypes.filter((m) => !this.newContentListing.resourceTypes.find((x) => x.id === m.id));
      },
    },

    watch: {
      'newContentListing.type': function (val) {
        if (val === ContentListingType.STATIC) {
          this.newContentListing.certificateText = '';
          this.newContentListing.grantsCertificate = false;
        }
      },
      
      selectedCategoryId(newVal) {
        if (newVal) {
          this.addToCategoriesArray(newVal);
          this.selectedCategoryId = '';
        }
      },
      
      selectedResourceTypeId(newVal) {
        if (newVal) {
          this.addToResourceTypesArray(newVal);
          this.selectedResourceTypeId = '';
        }
      },
    },

    mounted() {
      this.getCategories();
      this.getResourceTypes();
    },

    methods: {
      async getCategories() {
        const { data } = await axios.get(`/api/subject-areas/${this.contentListing.subjectAreaId}/categories/paged?currentPage=1&pageSize=10000&sortDir=name`);
        this.categories = data.resultItems;
      },

      async getResourceTypes() {
        const { data } = await axios.get(`/api/subject-areas/${this.contentListing.subjectAreaId}/resource-types/paged?currentPage=1&pageSize=10000&sortDir=name`);
        this.resourceTypes = data.resultItems;
      },

      async saveChanges() {
        this.saving = true;
        const formSubmission = JSON.parse(JSON.stringify(this.newContentListing));
        formSubmission.categoryIds = formSubmission.categories.map((category) => category.id);
        formSubmission.resourceTypeIds = formSubmission.resourceTypes.map((resourceType) => resourceType.id);
        try {
          await axios.patch(`/api/content-listings/${this.contentListing.id}`, formSubmission);
          await this.getContentListingInfo();
          this.$toast.success('Content listing changes were saved');
        } catch (error) {
          showErrorToast(error);
        } finally {
          this.saving = false;
        }
      },

      addToCategoriesArray(val) {
        if (!val) return;
        const category = this.categoryOptions.find((m) => m.id === val);
        if (category) {
          this.newContentListing.categories.push(category);
        }
      },

      addToResourceTypesArray(val) {
        if (!val) return;
        const resourceType = this.resourceTypeOptions.find((m) => m.id === val);
        if (resourceType) {
          this.newContentListing.resourceTypes.push(resourceType);
        }
      },

      removeCategory(categoryId) {
        this.newContentListing.categories = this.newContentListing.categories.filter(m => m.id !== categoryId);
      },

      removeResourceType(resourceTypeId) {
        this.newContentListing.resourceTypes = this.newContentListing.resourceTypes.filter(m => m.id !== resourceTypeId);
      },
    },
  };
</script>